import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, imgUrl, money } from "@/lib/api";
import { Shield, GraduationCap, Sprout, ArrowRight, ArrowUpRight, Heart, Check, Handshake, Megaphone } from "lucide-react";
import ProjectCard from "@/components/ProjectCard";
import SocialSection from "@/components/SocialSection";
import AnthemSection from "@/components/AnthemSection";
import { FLAG_HERO, CAMPAIGN, PHOTO_BABY, MERCY_MADE_INJERA, MERCY_MADE_SHOWCASE, FOUNDER_PHOTO } from "@/lib/assets";

const HERO = "https://static.prod-images.emergentagent.com/jobs/4ddf118d-5496-4d3e-a3e1-e4cf32f87a24/images/619a1f842ffc01e3fde38cff0cf741ec64c99fb9468a9d253d4168b29011dd89.jpeg";
const MERCY = PHOTO_BABY;

const pillars = [
  { icon: Shield, title: "Protect", items: ["Safe housing", "Food & clothing", "Healthcare", "Counseling", "Case management"] },
  { icon: GraduationCap, title: "Educate", items: ["School enrollment", "Supplies & backpacks", "Computers & tablets", "Tutoring", "Library access"] },
  { icon: Sprout, title: "Empower", items: ["Mentorship", "Life skills", "Recreation", "Future preparation", "Community support"] },
];

const needStats = [
  { value: "37M", label: "Children experiencing multidimensional poverty", source: "UNICEF" },
  { value: "13M+", label: "Children currently out of school", source: "UNICEF" },
  { value: "38.6%", label: "Of children under five affected by stunting", source: "UNICEF" },
  { value: "6.3M", label: "Children targeted for humanitarian assistance in 2026", source: "UNICEF" },
];

const monthlyTiers = [
  { amount: 25, label: "Nourish", desc: "Provides nutritious meals for a child for a full month.", featured: false },
  { amount: 50, label: "Educate", desc: "Keeps a child in school with supplies, books and tutoring support.", featured: true },
  { amount: 100, label: "Shelter", desc: "Helps sustain safe housing, healthcare and daily care for a child.", featured: false },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState({});
  const [mercy, setMercy] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/stories").then(({ data }) => { setMercy(data.find((s) => s.is_memorial) || data[0]); setStories(data.filter((s) => !s.is_memorial)); }).catch(() => {});
    api.get("/supporters").then(({ data }) => setSupporters(data)).catch(() => {});
  }, []);

  const donate = settings.gofundme_url || "https://gofund.me/15d8987b7";
  const featured = projects.filter((p) => p.featured).slice(0, 3);
  const featuredList = featured.length ? featured : projects.slice(0, 3);
  const totalRaised = projects.reduce((a, p) => a + (Number(p.raised) || 0), 0);
  const featuredSupporter = supporters.find((s) => s.featured) || supporters[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden">
        <img src={HERO} alt="Ethiopia" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D2E] via-[#1F4D2E]/75 to-[#1F4D2E]/45" />
        <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pb-20 pt-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-5">Refugium Ethiopia · Protect · Educate · Empower</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] tracking-tight">
              Help us build <span className="italic text-[#F2ECDD]">our first home.</span>
            </h1>
            <p className="text-lg text-[#E7EDE2]/90 mt-6 max-w-xl leading-relaxed">
              Refugium Ethiopia is working to create a safe and supportive home for vulnerable girls in Ethiopia — a place where they can be protected, educated, supported, and empowered to build their futures.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="hero-donate-btn"
                className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2">
                <Heart size={18} fill="white" /> Help build our first home
              </a>
              <Link to="/story" data-testid="hero-story-btn"
                className="bg-transparent text-white border border-white/60 rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-white hover:text-[#1F4D2E] transition-colors inline-flex items-center gap-2">
                Learn our story <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* THE NEED IN ETHIOPIA */}
      <section className="bg-[#FBF8F0] border-b border-[#1F4D2E]/10" data-testid="need-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">The Need in Ethiopia</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Understanding the challenges facing Ethiopia's children and families.</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {needStats.map((s, i) => (
              <motion.div key={s.value} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white border border-[#1F4D2E]/10 p-7 flex flex-col" data-testid={`need-stat-${i}`}>
                <span className="font-display text-4xl sm:text-5xl font-bold text-[#C1272D] leading-none mb-3">{s.value}</span>
                <p className="text-[#1F4D2E] text-sm leading-relaxed flex-1">{s.label}</p>
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#4F6B54]/60 mt-4">Source · {s.source}</span>
              </motion.div>
            ))}
          </div>
          <div className="max-w-3xl border-l-4 border-[#E0A526] pl-8">
            <h3 className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] mb-4">Behind every number is a child.</h3>
            <p className="text-[#4F6B54] leading-relaxed text-lg">These aren't just statistics. They represent girls, boys, mothers, fathers, and families who deserve safety, food, education, opportunity, and hope. It takes a village to raise a child. Together, we can help rebuild that village.</p>
            <Link to="/the-need" data-testid="need-learn-more" className="mt-6 inline-flex items-center gap-2 text-[#1F4D2E] font-semibold hover:text-[#C1272D] transition-colors">Understand the full picture <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* MISSION PLEDGE BANNER */}
      <section className="bg-[#1F4D2E] text-white relative overflow-hidden" data-testid="mission-banner">
        <span className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#E0A526]/10" />
        <span className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-[#C1272D]/15" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-6">Our Pledge</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            No child hungry.<br />No child homeless.<br /><span className="text-[#E0A526]">No child left behind.</span>
          </h2>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="mission-donate-btn"
              className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2">
              <Heart size={18} fill="white" /> Stand with us
            </a>
            <Link to="/about" data-testid="mission-learn-btn"
              className="border border-white/50 rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-white hover:text-[#1F4D2E] transition-colors inline-flex items-center gap-2">
              Our mission <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* MISSION PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Protect · Educate · Empower</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">How we work across every stage of a child's life.</h2>
          <p className="text-[#4F6B54] mt-5 leading-relaxed">Refugium Ethiopia protects, educates and empowers vulnerable children — and strengthens the communities around them. Our safe-home program is one part of a wider mission, built in step with Ethiopia's family and community-based alternative-care framework.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}
              className="bg-[#FBF8F0] border border-[#1F4D2E]/10 p-10 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300" data-testid={`pillar-${p.title.toLowerCase()}`}>
              <span className="w-14 h-14 rounded-full bg-[#E0A526]/20 flex items-center justify-center text-[#1F4D2E] mb-6"><p.icon size={26} /></span>
              <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-4">{p.title}</h3>
              <ul className="space-y-2.5">
                {p.items.map((it) => (
                  <li key={it} className="flex items-center gap-2.5 text-[#4F6B54]"><Check size={16} className="text-[#16A34A] shrink-0" /> {it}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FOUNDING NETWORK ANNOUNCEMENT */}
      <section className="bg-[#0F2E1B] text-white relative overflow-hidden" data-testid="founding-announcement">
        <span className="absolute -top-24 -right-20 w-96 h-96 rounded-full bg-[#E0A526]/10" />
        <span className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-[#C1272D]/15" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-5">We're seeking founding leaders, advisors, ambassadors &amp; partners</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-4xl">Help build Refugium Ethiopia from the beginning.</h2>
          <p className="text-lg text-[#E7EDE2]/90 mt-6 max-w-2xl leading-relaxed">We are seeking individuals willing to contribute their experience, expertise, connections and time toward creating lasting opportunities for children, families and communities in Ethiopia. Your experience can help us build an organization capable of protecting children, strengthening families, creating opportunities and empowering communities.</p>
          <div className="flex flex-wrap gap-2.5 mt-8 max-w-4xl">
            {["Law", "Medicine", "Counseling", "Social Work", "Education", "Nonprofit Leadership", "Grants", "Fundraising", "Business", "Finance", "Marketing", "Social Media", "Government / Public Service", "Economic Development", "Ethiopian Diaspora"].map((t) => (
              <span key={t} className="text-[13px] font-medium bg-white/10 border border-white/15 rounded-full px-4 py-1.5">{t}</span>
            ))}
          </div>
          <Link to="/join" data-testid="home-founding-cta" className="mt-9 inline-flex items-center gap-2 bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors">
            Join Our Founding Network <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* MERCY BENTO */}
      {mercy && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            <div className="md:col-span-8 relative overflow-hidden min-h-[380px] group">
              <img src={MERCY_MADE_INJERA} alt="Mercy Made — In loving memory of Mercy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D2E] via-[#1F4D2E]/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-10">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-2">In loving memory of Mercy</p>
                <h3 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-3">{mercy.title}</h3>
                <p className="text-[#E7EDE2]/90 max-w-lg mb-5">{mercy.subtitle || "Her story inspires our mission."}</p>
                <Link to="/story" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#F2ECDD] transition-colors w-fit" data-testid="mercy-read-more">Read Mercy's story <ArrowUpRight size={18} /></Link>
              </div>
            </div>
            <div className="md:col-span-4 bg-[#1F4D2E] text-white p-10 flex flex-col justify-center">
              {totalRaised > 0 ? (
                <>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Raised so far</p>
                  <p className="font-display text-5xl font-bold mb-2" data-testid="total-raised">{money(totalRaised)}</p>
                  <p className="text-[#E7EDE2]/70 mb-8">contributed by our community across active room-sponsorship projects.</p>
                </>
              ) : (
                <>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">We're just getting started</p>
                  <p className="font-display text-4xl font-bold mb-2 leading-tight" data-testid="total-raised">Be our first supporter.</p>
                  <p className="text-[#E7EDE2]/70 mb-8">Every project below is waiting for its very first gift — yours could be the one that begins it all.</p>
                </>
              )}
              <Link to="/transparency" className="inline-flex items-center gap-2 border border-white/40 rounded-full px-6 py-3 font-semibold hover:bg-white hover:text-[#1F4D2E] transition-colors w-fit" data-testid="see-impact">See our commitment <ArrowRight size={17} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* MERCY MADE SOCIAL ENTERPRISE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="mercy-made-section">
        <div className="max-w-2xl mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Mercy Made · Made in Ethiopia</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Every purchase has a purpose.</h2>
          <p className="text-[#4F6B54] mt-5 leading-relaxed">Mercy Made is the social-enterprise initiative of Refugium Ethiopia — handmade coffee, soap, candles and injera. Every purchase helps protect, educate and empower vulnerable children.</p>
        </div>
        <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="mercy-made-banner" className="block group overflow-hidden border border-[#1F4D2E]/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <img src={MERCY_MADE_SHOWCASE} alt="Mercy Made products — every purchase has a purpose" className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700 ease-out" />
        </a>
        <div className="mt-6">
          <Link to="/mercy-made" data-testid="mercy-made-shop-link" className="inline-flex items-center gap-2 text-[#1F4D2E] font-semibold hover:text-[#C1272D] transition-colors">Explore Mercy Made <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* IMPACT STORIES */}
      {stories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="impact-stories">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Stories from the ground</p>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E]">Real lives, real change.</h2>
            </div>
            <Link to="/story" className="text-[#1F4D2E] font-semibold inline-flex items-center gap-2 hover:text-[#C1272D] transition-colors" data-testid="view-all-stories">Read more stories <ArrowRight size={18} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stories.slice(0, 3).map((st, i) => (
              <motion.article key={st.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-[#FBF8F0] border border-[#1F4D2E]/10 overflow-hidden hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300" data-testid={`impact-story-${st.id}`}>
                {st.image_url && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={imgUrl(st.image_url)} alt={st.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-7">
                  <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-2">{st.title}</h3>
                  {st.subtitle && <p className="text-sm font-medium text-[#C1272D] mb-3">{st.subtitle}</p>}
                  <p className="text-[#4F6B54] text-sm leading-relaxed line-clamp-4 whitespace-pre-line">{st.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Sponsor a room</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E]">Build something that lasts.</h2>
          </div>
          <Link to="/projects" className="text-[#1F4D2E] font-semibold inline-flex items-center gap-2 hover:text-[#C1272D] transition-colors" data-testid="view-all-projects">View all projects <ArrowRight size={18} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredList.map((p) => <ProjectCard key={p.id} project={p} donateFallback={donate} />)}
        </div>
      </section>

      {/* FEATURED SUPPORTER STRIP */}
      {featuredSupporter && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16" data-testid="home-supporter-strip">
          <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <span className="w-14 h-14 rounded-full bg-[#E0A526]/20 text-[#1F4D2E] flex items-center justify-center shrink-0"><Heart size={26} fill="#1F4D2E" /></span>
            <div className="flex-1">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-1">{featuredSupporter.featured ? "Supporter of the month" : "With gratitude"}</p>
              <p className="font-display text-2xl font-semibold text-[#1F4D2E]">{featuredSupporter.name}</p>
              {featuredSupporter.message && <p className="text-[#4F6B54] italic mt-1">“{featuredSupporter.message}”</p>}
            </div>
            <Link to="/transparency" data-testid="home-supporter-link" className="inline-flex items-center gap-2 text-[#1F4D2E] font-semibold hover:text-[#C1272D] transition-colors shrink-0">See the wall <ArrowRight size={18} /></Link>
          </div>
        </section>
      )}

      {/* REFUGIUM VILLAGE — MONTHLY SUPPORTERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-8" data-testid="monthly-section">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Refugium Village · Monthly Supporters</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Become a sustaining supporter.</h2>
          <p className="text-[#4F6B54] mt-5 leading-relaxed">It takes a village to raise a child. A monthly gift gives our children something priceless — the certainty that someone will be there, month after month. Set up recurring giving on GoFundMe and join the Refugium Village.</p>
          {settings.monthly_supporters > 0 && (
            <div className="inline-flex items-center gap-2 mt-6 bg-[#16A34A]/10 text-[#16A34A] rounded-full px-5 py-2.5 font-semibold" data-testid="monthly-tally">
              <Heart size={17} fill="#16A34A" /> {settings.monthly_supporters} {settings.monthly_supporters === 1 ? "family is" : "families are"} already giving monthly
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {monthlyTiers.map((t, i) => (
            <motion.div key={t.amount} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative p-8 border transition-all duration-300 ${t.featured ? "bg-[#1F4D2E] text-white border-[#1F4D2E] shadow-[0_20px_45px_rgb(0,0,0,0.12)]" : "bg-[#FBF8F0] text-[#1F4D2E] border-[#1F4D2E]/10 hover:-translate-y-1"}`}
              data-testid={`monthly-tier-${t.amount}`}>
              {t.featured && <span className="absolute top-0 right-0 bg-[#E0A526] text-[#1F4D2E] text-[10px] tracking-[0.15em] uppercase font-bold px-3 py-1">Most loved</span>}
              <p className={`text-xs tracking-[0.2em] uppercase font-bold mb-4 ${t.featured ? "text-[#E0A526]" : "text-[#C1272D]"}`}>{t.label}</p>
              <div className="flex items-end gap-1 mb-4">
                <span className="font-display text-5xl font-bold leading-none">${t.amount}</span>
                <span className={`text-sm mb-1 ${t.featured ? "text-[#E7EDE2]/70" : "text-[#4F6B54]"}`}>/ month</span>
              </div>
              <p className={`text-sm leading-relaxed mb-8 ${t.featured ? "text-[#E7EDE2]/90" : "text-[#4F6B54]"}`}>{t.desc}</p>
              <a href={donate} target="_blank" rel="noopener noreferrer" data-testid={`monthly-give-${t.amount}`}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${t.featured ? "bg-[#C1272D] text-white hover:bg-[#9E1F24]" : "bg-[#1F4D2E] text-white hover:bg-[#163a22]"}`}>
                <Heart size={16} fill="white" /> Give ${t.amount}/mo
              </a>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-[#4F6B54]/70 mt-6 max-w-2xl">On GoFundMe, choose "make this monthly" when you give — or set your own amount. Every recurring gift, large or small, helps us plan ahead for the children in our care.</p>
      </section>

      <SocialSection />

      <AnthemSection />

      {/* FOUNDER PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-founder-preview">
        <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-3 flex justify-center">
            <img src={FOUNDER_PHOTO} alt="Amelework &ldquo;Amilly&rdquo; Kassahun, Founder of Refugium Ethiopia" data-testid="home-founder-photo" className="w-40 h-40 rounded-full object-cover object-top border-4 border-[#E0A526]/40 shadow-lg mx-auto" />
          </div>
          <div className="md:col-span-9">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-2">Meet our founder</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] leading-tight">Amelework &ldquo;Amilly&rdquo; Kassahun</h2>
            <p className="text-[#4F6B54] mt-4 leading-relaxed">Amilly knows firsthand what it means for one opportunity to change a child's life. Abandoned as an infant and facing extraordinary challenges throughout her childhood in Addis Ababa, she eventually received the support and education that helped change the direction of her future. Today, she wants to open that same door for others.</p>
            <p className="font-display italic text-xl text-[#1F4D2E] mt-5">&ldquo;My life changed. Why can't theirs?&rdquo;</p>
            <Link to="/founder" data-testid="home-founder-link" className="mt-6 inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-7 py-3.5 font-semibold hover:bg-[#C1272D] transition-colors">Read Amilly's story <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* HELP BUILD OUR FIRST HOME CAMPAIGN */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="campaign-section">
        <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="campaign-banner"
          className="block group overflow-hidden border border-[#1F4D2E]/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <img src={CAMPAIGN} alt="Help build our first home — In loving memory of Mercy" className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700 ease-out" />
        </a>
      </section>

      {/* HOW WILL YOU MAKE AN IMPACT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="impact-ways-section">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Get involved</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">How will you make an impact?</h2>
          <p className="text-[#4F6B54] mt-5 leading-relaxed">There are many ways to be part of Refugium Ethiopia — give, serve, partner or amplify our mission.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Heart, t: "Give", d: "Support Refugium programs financially.", cta: "Donate", href: donate, external: true },
            { icon: Sprout, t: "Serve", d: "Contribute your professional expertise.", cta: "Join our founding network", to: "/join" },
            { icon: Handshake, t: "Partner", d: "Organizations and businesses can work with Refugium.", cta: "Become a partner", to: "/join?interest=Corporate%20Partnership" },
            { icon: Megaphone, t: "Amplify", d: "Help bring Refugium's mission to more people.", cta: "Become an ambassador", to: "/join?interest=Global%20Ambassador%20%2F%20Influencer" },
          ].map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-xl p-8 flex flex-col hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300" data-testid={`impact-way-${c.t.toLowerCase()}`}>
              <span className="w-14 h-14 rounded-full bg-[#E0A526]/20 text-[#1F4D2E] flex items-center justify-center mb-5"><c.icon size={26} /></span>
              <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-2">{c.t}</h3>
              <p className="text-[#4F6B54] text-sm leading-relaxed flex-1 mb-5">{c.d}</p>
              {c.external ? (
                <a href={c.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#C1272D] font-semibold hover:gap-3 transition-all">{c.cta} <ArrowRight size={17} /></a>
              ) : (
                <Link to={c.to} className="inline-flex items-center gap-2 text-[#1F4D2E] font-semibold hover:gap-3 hover:text-[#C1272D] transition-all">{c.cta} <ArrowRight size={17} /></Link>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#C1272D] text-white p-12 md:p-20 text-center relative overflow-hidden">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mb-4 relative">Help us open the door for another child.</h2>
          <p className="text-white/90 max-w-xl mx-auto mb-8 relative">Amilly's life changed because people chose to care. Together, we can help create opportunities for vulnerable children in Ethiopia.</p>
          <div className="flex flex-wrap gap-4 justify-center relative">
            <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="cta-donate-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1F4D2E] rounded-full px-9 py-4 font-semibold tracking-wide hover:bg-[#1F4D2E] hover:text-white transition-colors">
              <Heart size={18} /> Help build our first home
            </a>
            <Link to="/join" data-testid="cta-involved-btn"
              className="inline-flex items-center gap-2 border border-white/60 text-white rounded-full px-9 py-4 font-semibold tracking-wide hover:bg-white/10 transition-colors">
              Get involved <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
